import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import supabase from '@/core/api/supabase';
import { UserRole } from '@/types/enums';

/**
 * Authentication response type with detailed error information
 */
interface AuthResponse {
  data: any;
  error: AuthError | null;
  errorType?: string;
}

/**
 * Authentication Context Type
 * Defines the shape of the authentication context
 */
interface ProfileType {
  role: string;
  avatar_url?: string | null;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile?: ProfileType | null;
  userRole: UserRole;
  avatarUrl: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  sendMagicLink: (email: string, redirectTo?: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  getInvitationMetadata: (token: string) => Promise<{ valid: boolean; email?: string; error?: AuthError }>;
  completeInvitation: (token: string, password: string) => Promise<{ error: AuthError | null }>;
  // Avatar update method
  updateAvatarUrl: (url: string | null) => void;
  // Development mode methods - exposed for testing and fallback authentication
  setUser: (user: User | null) => void;
  setUserRole: (role: UserRole) => void;
  setSession: (session: Session | null) => void;
}

// Create the auth context with undefined default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Helper function to normalize and map role strings to UserRole enum values
 */
function normalizeAndMapRole(role: string | null | undefined): UserRole {
  if (!role) return UserRole.USER;
  
  const normalizedRole = role.toLowerCase();
  
  if (normalizedRole === UserRole.ADMIN.toLowerCase()) {
    return UserRole.ADMIN;
  } else if (normalizedRole === UserRole.FACILITATOR.toLowerCase()) {
    return UserRole.FACILITATOR;
  } else {
    return UserRole.USER;
  }
}

/**
 * Auth Provider Component
 * Manages authentication state and provides auth methods to the application
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(UserRole.USER);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the user session
    async function getSession() {
      try {


        const { data: { session } } = await supabase.auth.getSession();
        
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Fetch user profile
          try {

            const { data, error } = await supabase
              .from('profiles')
              .select('role, avatar_url, display_name, first_name, last_name')
              .eq('id', session.user.id)
              .single();

            if (error) {

              setUserRole(UserRole.USER);
              setAvatarUrl(null);
            } else {
              // Handle role with explicit mapping
              // Store full profile data
              setProfile(data);
              
              if (data?.role) {

                // Explicit role mapping for consistent enum usage
                const normalizedRole = data.role.toLowerCase();
                
                if (normalizedRole === UserRole.ADMIN.toLowerCase()) {

                  setUserRole(UserRole.ADMIN);
                } else if (normalizedRole === UserRole.FACILITATOR.toLowerCase()) {

                  setUserRole(UserRole.FACILITATOR);
                } else {

                  setUserRole(UserRole.USER);
                }
              } else {

                setUserRole(UserRole.USER); // Default role
              }
              
              // Handle avatar URL
              if (data?.avatar_url) {

                setAvatarUrl(data.avatar_url);
              } else {
                setAvatarUrl(null);
              }
            }
          } catch (err) {

          } finally {

            setLoading(false);
          }
        } else {

          setUserRole(UserRole.USER);
          setAvatarUrl(null);
          setProfile(null);
          setLoading(false);
        }
      } catch (err) {

        setLoading(false);
      }
    }

    // Call getSession immediately
    getSession();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {

        setSession(session);
        setUser(session?.user ?? null);
        
        // EMERGENCY FIX: Direct email-based admin role assignment
        if (session?.user?.email === 'admin@kadua.com') {

          setUserRole(UserRole.ADMIN);
        }
        
        // Handle profile fetching in a separate async function
        if (session?.user) {
          fetchUserProfile(session);
        } else {
          setUserRole(UserRole.USER);
          setAvatarUrl(null);
        }
      }
    );
    
    // Separate async function to fetch user profile
    async function fetchUserProfile(session: Session) {
      try {

        // First check if profile exists
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();
        
        if (profileError && profileError.code === 'PGRST116') {
          // PGRST116 means no profile was found - create one

          try {
            // Always use VIEWER as initial role for new profiles
            // This prevents unauthorized admin access
            // Roles should be explicitly assigned by admins in the user management interface
            const initialRole = UserRole.USER;

            // Ensure email is not undefined before inserting
            const userEmail = session.user.email || '';
            
            const { error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: session.user.id,
                email: userEmail,
                role: initialRole,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              });
            
            if (insertError) {

              setUserRole(UserRole.USER); // Default to regular user on error
            } else {

              setUserRole(initialRole);
            }
          } catch (createErr) {

            setUserRole(UserRole.USER);
          }
        } else if (profileData) {

          // Special logging for admin users to help with debugging
          if (session.user.email?.includes('admin')) {
            
          }
          
          // Map the role using our robust function
          const mappedRole = normalizeAndMapRole(profileData.role);

          setUserRole(mappedRole);
        } else {
          // Profile exists but no valid role

          setUserRole(UserRole.USER);
        }
      } catch (err) {

        setUserRole(UserRole.USER);
      } finally {

        setLoading(false);
      }
    }
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign in a user with email and password
   */
  const signIn = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      // Execute the actual sign-in
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {

        return { data: null, error, errorType: 'supabase-auth' };
      }

      setUser(data.user);
      setSession(data.session);
      
      // Try to get user profile with role
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', data.user?.id)
        .single();
      
      if (profileError) {

      }
      
      // Set user role based on profile or fallback to default USER role
      const role = profileData?.role || 'user';
      const normalizedRole = normalizeAndMapRole(role);
      setUserRole(normalizedRole);

      // Check for returnTo parameter in URL and redirect there if present
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('returnTo');
      
      if (returnTo) {
        // Security check: Only redirect to internal paths, not to external URLs
        if (returnTo.startsWith('/')) {

          window.location.href = returnTo;
        } else {
          // If returnTo is not a valid internal path, use role-based redirect

          // Role-based redirect
          redirectBasedOnRole(normalizedRole);
        }
      } else {
        // No returnTo parameter, use role-based redirect
        redirectBasedOnRole(normalizedRole);
      }

      return { data, error: null };
    } catch (err) {

      const authError = new Error('An unexpected authentication error occurred') as AuthError;
      return { data: null, error: authError, errorType: 'unexpected' };
    }
  };
  
  /**
   * Redirect user based on their role
   */
  const redirectBasedOnRole = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        window.location.href = '/group-sessions';
        break;
      case UserRole.FACILITATOR:
        window.location.href = '/facilitator/group-sessions';
        break;
      case UserRole.USER:
      default:
        window.location.href = '/my-group-sessions';
        break;
    }
  };

  /**
   * Sign up with email and password
   */
  const signUp = async (email: string, password: string): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password
      });
      return { error };
    } catch (err: any) {
      return { error: new Error(`Unexpected error during sign up: ${err?.message || err}`) as AuthError };
    }
  };

  /**
   * Sign out the current user
   * Performs a complete logout by:
   * 1. Signing out from Supabase (removes tokens from localStorage)
   * 2. Clearing auth state in the application
   * 3. Removing all session data including persistent tokens
   * 4. Cleaning up any application-specific local storage
   */
  const signOut = async (): Promise<{ error: AuthError | null }> => {

    try {
      // Use the scope option to clear all persistent sessions
      const { error } = await supabase.auth.signOut({ 
        scope: 'global' // This removes all tokens, including refresh tokens
      });
      
      if (error) {

        return { error };
      }
      
      // Clear local auth state
      setUser(null);
      setSession(null);
      setUserRole(UserRole.USER);
      setAvatarUrl(null);
      
      // Clear any application-specific items from localStorage
      // that might contain user state or preferences
      try {
        // List of app-specific localStorage keys to clear
        const appStorageKeys = [
          'ww-user-preferences',
          'ww-last-session',
          'ww-theme-preference'
        ];
        
        // Remove each key
        appStorageKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
          }
        });
      } catch (storageErr) {

        // Continue with logout even if local storage clearing fails
      }

      return { error: null };
    } catch (err: any) {

      return { error: new Error(`Unexpected error during sign out: ${err?.message || err}`) as AuthError };
    }
  };

  /**
   * Reset password for the given email
   */
  const resetPassword = async (email: string): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      return { error };
    } catch (err: any) {
      return { error: new Error(`Unexpected error during password reset: ${err?.message || err}`) as AuthError };
    }
  };

  /**
   * Send a magic link to the specified email
   */
  const sendMagicLink = async (email: string, redirectTo?: string): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo || `${window.location.origin}/auth/callback`
        }
      });
      return { error };
    } catch (err: any) {
      return { error: new Error(`Unexpected error sending magic link: ${err?.message || err}`) as AuthError };
    }
  };

  /**
   * Verify an invitation token and retrieve associated metadata
   */
  const getInvitationMetadata = async (token: string): Promise<{ valid: boolean; email?: string; error?: AuthError }> => {
    // Simplified implementation to avoid TypeScript errors
    // This is a placeholder that will be properly implemented later

    return {
      valid: false,
      error: new Error('Invitation functionality is not implemented yet') as AuthError
    };
  };

  /**
   * Complete the invitation process by setting up a new user account
   */
  const completeInvitation = async (token: string, password: string): Promise<{ error: AuthError | null }> => {
    // Simplified implementation to avoid TypeScript errors
    // This is a placeholder that will be properly implemented later

    return {
      error: new Error('Invitation functionality is not implemented yet') as AuthError
    };
  };

  // Construct the context value
  /**
   * Methods for development mode testing and fallback authentication
   * These are explicitly exposed for use by the development auth hook
   */
  const setUserExposed = useCallback((newUser: User | null) => {
    setUser(newUser);
  }, []);
  
  const setUserRoleExposed = useCallback((newRole: UserRole) => {
    setUserRole(newRole);
  }, []);
  
  const setSessionExposed = useCallback((newSession: Session | null) => {
    setSession(newSession);
  }, []);

  /**
   * Update the user's avatar URL in the context
   * This is called when the user uploads a new avatar
   */
  const updateAvatarUrl = useCallback((url: string | null) => {
    setAvatarUrl(url);
  }, []);

  const value: AuthContextType = {
    session,
    user,
    profile,
    userRole,
    avatarUrl,
    loading,
    signIn,
    sendMagicLink,
    signUp,
    signOut,
    resetPassword,
    getInvitationMetadata,
    completeInvitation,
    updateAvatarUrl,
    // Development mode methods
    setUser: setUserExposed,
    setUserRole: setUserRoleExposed,
    setSession: setSessionExposed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use the auth context
 * Must be used within an AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
